import { useMemo, useState } from 'react';
import { AlertCircle, ChevronRight, Globe, Layers, ListChecks, RefreshCw, Search, Server, X } from 'lucide-react';
import type { ScheduleTarget, TargetCatalog, TargetServer } from '../../types/scheduledCommands';
import { reachOf, targetKey, targetLabel } from '../../utils/scheduledCommands';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';

interface TargetPickerProps {
  value: ScheduleTarget[];
  onChange: (next: ScheduleTarget[]) => void;
  catalog: TargetCatalog | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  disabled?: boolean;
}

interface ClusterGroup {
  name: string;
  pack: string | null;
  members: TargetServer[];
}

const VersionTag = ({ version }: { version: string | null }) =>
  version ? <span className="font-mono tabular-nums text-glass-400">{version}</span> : <span className="text-glass-500">version unknown</span>;

const OnlineDot = ({ online }: { online: boolean }) => (
  <span
    className={cn('inline-block w-1.5 h-1.5 rounded-full shrink-0', online ? 'bg-green-400' : 'bg-glass-500')}
    title={online ? 'Online' : 'Offline'}
    aria-label={online ? 'Online' : 'Offline'}
  />
);

/**
 * Who a schedule reaches: every server, or any mix of clusters and single
 * servers.
 *
 * The list is the one Mr. Roboto dispatches against, so what is ticked here is
 * exactly what the bot will resolve. A ticked cluster covers its members,
 * including servers added to it later, which is why its members show as
 * included rather than ticked one by one.
 */
export const TargetPicker = ({ value, onChange, catalog, loading, error, onRetry, disabled = false }: TargetPickerProps) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  // What was picked before switching to "All servers", so switching back restores it.
  const [remembered, setRemembered] = useState<ScheduleTarget[]>([]);

  const everyone = value.some((t) => t.type === 'all');
  const picked = useMemo(() => new Set(value.map(targetKey)), [value]);
  const reach = useMemo(() => reachOf(value, catalog), [value, catalog]);

  const { clusters, standalone } = useMemo(() => {
    if (!catalog) return { clusters: [] as ClusterGroup[], standalone: [] as TargetServer[] };
    const byAddress = new Map(catalog.servers.map((s) => [s.address.toLowerCase(), s]));
    const inCluster = new Set<string>();
    const groups: ClusterGroup[] = catalog.clusters
      .map((cluster) => {
        const members = cluster.servers
          .map((address) => byAddress.get(address.toLowerCase()))
          .filter((s): s is TargetServer => !!s)
          .sort((a, b) => a.address.localeCompare(b.address));
        members.forEach((m) => inCluster.add(m.address.toLowerCase()));
        return { name: cluster.name, pack: members.find((m) => m.pack)?.pack ?? null, members };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    const rest = catalog.servers
      .filter((s) => !inCluster.has(s.address.toLowerCase()))
      .sort((a, b) => a.address.localeCompare(b.address));
    return { clusters: groups, standalone: rest };
  }, [catalog]);

  const query = search.trim().toLowerCase();
  const matches = (s: TargetServer) =>
    !query || s.address.toLowerCase().includes(query) || (s.pack ?? '').toLowerCase().includes(query);
  const visibleClusters = clusters.filter(
    (c) => !query || c.name.toLowerCase().includes(query) || (c.pack ?? '').toLowerCase().includes(query) || c.members.some(matches),
  );
  const visibleStandalone = standalone.filter(matches);

  const setMode = (mode: 'all' | 'some') => {
    if (mode === 'all' && !everyone) {
      setRemembered(value);
      onChange([{ type: 'all' }]);
    } else if (mode === 'some' && everyone) {
      onChange(remembered);
    }
  };

  const toggle = (target: ScheduleTarget) => {
    const key = targetKey(target);
    if (picked.has(key)) {
      onChange(value.filter((t) => targetKey(t) !== key));
      return;
    }
    // Ticking a cluster makes its individually ticked members redundant.
    let next = value.filter((t) => t.type !== 'all');
    if (target.type === 'cluster') {
      const group = clusters.find((c) => c.name.toLowerCase() === target.name.toLowerCase());
      const members = new Set(group?.members.map((m) => m.address.toLowerCase()) ?? []);
      next = next.filter((t) => !(t.type === 'server' && members.has(t.address.toLowerCase())));
    }
    onChange([...next, target]);
  };

  const toggleOpen = (name: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const versionLine = reach.versions.map((v) => `${v.version} ×${v.count}`).join(' · ');

  return (
    <div className="space-y-3">
      <div className="segmented max-w-full" role="radiogroup" aria-label="Which servers">
        <button
          type="button"
          role="radio"
          aria-checked={everyone}
          className="segmented-item"
          data-active={everyone}
          onClick={() => setMode('all')}
          disabled={disabled}
        >
          <Globe className="w-4 h-4" />
          All servers
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={!everyone}
          className="segmented-item"
          data-active={!everyone}
          onClick={() => setMode('some')}
          disabled={disabled}
        >
          <ListChecks className="w-4 h-4" />
          Choose clusters and servers
        </button>
      </div>

      {!everyone && value.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Chosen targets">
          {value.map((target) => {
            const missing = reach.missing.some((m) => targetKey(m) === targetKey(target));
            return (
              <span
                key={targetKey(target)}
                className={cn('chip chip-removable', missing && 'border-amber-500/40 text-amber-300')}
                title={missing ? 'Not in the server list any more. Nothing will be sent here.' : undefined}
              >
                {target.type === 'cluster' ? <Layers className="w-3 h-3" /> : <Server className="w-3 h-3" />}
                <span className={cn(target.type === 'server' && 'font-mono')}>{targetLabel(target)}</span>
                {!disabled && (
                  <button type="button" onClick={() => toggle(target)} aria-label={`Remove ${targetLabel(target)}`}>
                    <X className="w-3 h-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {!everyone && (
        <div className="subcard overflow-hidden">
          <div className="relative border-b border-glass-200/10">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              className="w-full bg-transparent pl-9 pr-3 py-2.5 text-sm outline-none placeholder:text-glass-500"
              placeholder="Search clusters, packs and servers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search clusters, packs and servers"
              disabled={disabled}
            />
          </div>

          {loading && !catalog ? (
            <div className="p-3 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <SkeletonLoader key={i} className="h-8" />)}
            </div>
          ) : error && !catalog ? (
            <div className="p-3 flex items-center gap-2 text-sm text-red-400">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="flex-1">{error}</span>
              <button type="button" onClick={onRetry} className="glass-button-secondary">
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto divide-y divide-glass-200/10" aria-label="Clusters and servers">
              {visibleClusters.map((cluster) => {
                const key = targetKey({ type: 'cluster', name: cluster.name });
                const checked = picked.has(key);
                const expanded = open.has(cluster.name) || (!!query && cluster.members.some(matches));
                const versions = [...new Set(cluster.members.map((m) => m.version).filter(Boolean))];
                return (
                  <li key={key}>
                    <div className="flex items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={checked}
                        onChange={() => toggle({ type: 'cluster', name: cluster.name })}
                        disabled={disabled}
                        aria-label={`Cluster ${cluster.name}`}
                      />
                      <button
                        type="button"
                        onClick={() => toggleOpen(cluster.name)}
                        className="flex-1 min-w-0 flex items-center gap-2 text-left"
                        aria-expanded={expanded}
                      >
                        <Layers className="w-4 h-4 shrink-0 text-glass-400" />
                        <span className="text-sm font-medium text-white truncate">{cluster.name}</span>
                        {cluster.pack && <span className="text-xs text-glass-400 truncate hidden sm:inline">{cluster.pack}</span>}
                        <span className="ml-auto text-xs text-glass-500 tabular-nums shrink-0">
                          {cluster.members.length} server{cluster.members.length === 1 ? '' : 's'}
                          {versions.length > 0 && <span className="hidden sm:inline"> · <span className="font-mono">{versions.join(', ')}</span></span>}
                        </span>
                        <ChevronRight className={cn('w-4 h-4 shrink-0 text-glass-500 transition-transform', expanded && 'rotate-90')} />
                      </button>
                    </div>
                    {expanded && (
                      <ul className="pb-1">
                        {cluster.members.filter((m) => !query || matches(m) || cluster.name.toLowerCase().includes(query)).map((server) => {
                          const serverKey = targetKey({ type: 'server', address: server.address });
                          return (
                            <li key={serverKey} className="flex items-center gap-3 pl-10 pr-3 py-1.5">
                              <input
                                type="checkbox"
                                className="form-checkbox"
                                checked={checked || picked.has(serverKey)}
                                disabled={disabled || checked}
                                onChange={() => toggle({ type: 'server', address: server.address })}
                                aria-label={server.address}
                                title={checked ? 'Included with the cluster' : undefined}
                              />
                              <OnlineDot online={server.online} />
                              <span className="flex-1 min-w-0 truncate font-mono text-xs text-glass-300">{server.address}</span>
                              <span className="text-xs shrink-0"><VersionTag version={server.version} /></span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })}

              {visibleStandalone.length > 0 && (
                <li className="px-3 pt-3 pb-1">
                  <span className="eyebrow">Not in a cluster</span>
                </li>
              )}
              {visibleStandalone.map((server) => {
                const key = targetKey({ type: 'server', address: server.address });
                return (
                  <li key={key} className="flex items-center gap-3 px-3 py-2">
                    <input
                      type="checkbox"
                      className="form-checkbox"
                      checked={picked.has(key)}
                      onChange={() => toggle({ type: 'server', address: server.address })}
                      disabled={disabled}
                      aria-label={server.address}
                    />
                    <OnlineDot online={server.online} />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-mono text-xs text-glass-300">{server.address}</span>
                      {server.pack && <span className="block truncate text-xs text-glass-500">{server.pack}</span>}
                    </span>
                    <span className="text-xs shrink-0"><VersionTag version={server.version} /></span>
                  </li>
                );
              })}

              {visibleClusters.length === 0 && visibleStandalone.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-glass-400">
                  {query ? 'Nothing matches that search.' : 'The bot reported no servers.'}
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      <p className="text-xs text-glass-400" aria-live="polite">
        {catalog ? (
          value.length === 0 ? (
            'Choose at least one cluster or server.'
          ) : (
            <>
              Reaches <span className="text-white tabular-nums">{reach.servers.length}</span> server{reach.servers.length === 1 ? '' : 's'}
              {reach.servers.length > 0 && <>, <span className="tabular-nums">{reach.online}</span> online now</>}
              {versionLine && <> · <span className="font-mono">{versionLine}</span></>}
              {reach.unknownVersions > 0 && <> · {reach.unknownVersions} not reporting a version</>}
            </>
          )
        ) : loading ? (
          'Loading the server list…'
        ) : null}
      </p>
    </div>
  );
};
